/**
 * Session revocation helpers (permanent-session-continuity design §1.2, §3.1, §3.2, §4.7 Web).
 */
import { decodeJwtPayload, recordFreshAccessToken } from './jwtClock';

const LOGOUT_CODE_KEY = 'lastLogoutCode';

/** The `sessionId` claim of a JWT, or null. Base64url-safe; never throws. */
export function getTokenSessionId(token) {
  try {
    const sid = decodeJwtPayload(token)?.sessionId;
    return typeof sid === 'string' && sid ? sid : null;
  } catch (e) {
    console.warn('[sessionRevocation] getTokenSessionId failed:', e?.message || e);
    return null;
  }
}

/** `{ userId, sessionId }` of a JWT, or null when undecodable. Never throws. */
export function tokenIdentity(token) {
  try {
    const p = decodeJwtPayload(token);
    if (!p) return null;
    const userId = p.id ?? null;
    const sessionId = typeof p.sessionId === 'string' && p.sessionId ? p.sessionId : null;
    return { userId, sessionId };
  } catch (e) {
    console.warn('[sessionRevocation] tokenIdentity failed:', e?.message || e);
    return null;
  }
}

/**
 * Design §3.1 rule (identical on mobile). true → log out.
 * Returns true on an internal error (matches today's behaviour).
 */
export function decideDeviceSessionsClearedLogout({ currentSessionId, keepSessionId, sessionIds } = {}) {
  try {
    const hasList = Array.isArray(sessionIds) && sessionIds.some((s) => typeof s === 'string' && s);
    const hasKeep = typeof keepSessionId === 'string' && keepSessionId !== '';
    if (!hasList && !hasKeep) return true; // legacy payload → current behaviour (logout)
    if (!currentSessionId) return true; // cannot identify self → legacy behaviour (logout)
    if (hasList && sessionIds.includes(currentSessionId)) return true;
    if (hasKeep && currentSessionId !== keepSessionId) return true;
    return false;
  } catch (e) {
    console.error('[sessionRevocation] decideDeviceSessionsClearedLogout failed:', e?.message || e);
    return true;
  }
}

/** First-wins writer of localStorage.lastLogoutCode. Never throws. */
export function recordLastLogoutCode(code) {
  try {
    if (typeof code !== 'string' || !code) return;
    if (!localStorage.getItem(LOGOUT_CODE_KEY)) localStorage.setItem(LOGOUT_CODE_KEY, code);
  } catch (e) {
    console.warn('[sessionRevocation] recordLastLogoutCode failed:', e?.message || e);
  }
}

/**
 * Single same-tab token writer for logins (design §4.7 Web).
 * 1. writes accessToken; 2. writes refreshToken (or removes it when clearMissingRefresh);
 * 3. records skew; 4. clears lastLogoutCode; 5. dispatches `myams:session-changed`.
 */
export function applyWebSessionTokens({ accessToken, refreshToken, clearMissingRefresh = false } = {}) {
  try {
    if (typeof accessToken === 'string' && accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (typeof refreshToken === 'string' && refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    } else if (clearMissingRefresh) {
      localStorage.removeItem('refreshToken');
    }
    recordFreshAccessToken(accessToken);
    localStorage.removeItem(LOGOUT_CODE_KEY);
    window.dispatchEvent(new CustomEvent('myams:session-changed'));
  } catch (e) {
    console.warn('[sessionRevocation] applyWebSessionTokens failed:', e?.message || e);
  }
}
